import { bodiesOverlap, bottomForCenterY, centerX, centerY, clampAbs } from '../geom'
import { NO_PLAYER } from '../world'
import type { World } from '../world'
import { hit } from './hit'
import type { ProjectileItem } from './types'

/** The racing player in first place other than `owner`, or `NO_PLAYER`. */
export function leaderOf(world: World, owner: number): number {
  for (let k = 0; k < world.players.length; k++) {
    const p = world.players[k]!
    if (k !== owner && p.placement === 0 && p.rank === 1) return k
  }
  return NO_PLAYER
}

/**
 * Leader Missile (PHASE3 T3.12, a last-place item): flies (no terrain) from the owner toward
 * whoever leads the race, re-aiming every tick, at `speedPxPerTick` on each axis. On contact it
 * blows up: the target is stunned `stunTicks`, every other racer whose centre is within
 * `splashRadiusPx` (both axes) `splashStunTicks`, all through `hit()` with the owner as
 * attacker (shields and rider rules apply). With no one ahead to chase it flies right until
 * `lifetimeTicks`.
 */
export const leaderMissile: ProjectileItem = {
  id: 'leader_missile',
  use(world, i) {
    const p = world.players[i]!
    const lm = world.cfg.items.leaderMissile
    world.projectiles.push({
      id: world.nextId++,
      kind: 'leader_missile',
      owner: i,
      x: p.x + p.w,
      y: bottomForCenterY(centerY(p), lm.sizePx),
      w: lm.sizePx,
      h: lm.sizePx,
      vx: lm.speedPxPerTick,
      vy: 0,
      grounded: false,
      target: leaderOf(world, i),
      locked: false,
      expiresAtTick: world.tick + lm.lifetimeTicks,
    })
  },
  update(world, pr, events) {
    if (world.tick >= pr.expiresAtTick) return false
    const lm = world.cfg.items.leaderMissile
    pr.target = leaderOf(world, pr.owner)
    const t = pr.target === NO_PLAYER ? null : world.players[pr.target]!
    if (t) {
      pr.vx = clampAbs(centerX(t) - centerX(pr), lm.speedPxPerTick)
      pr.vy = clampAbs(centerY(t) - centerY(pr), lm.speedPxPerTick)
    }
    pr.x += pr.vx
    pr.y += pr.vy
    if (!t || !bodiesOverlap(t, pr)) return true
    hit(world, pr.target, 'leader_missile', lm.stunTicks, events, pr.owner)
    const cx = centerX(pr)
    const cy = centerY(pr)
    for (let k = 0; k < world.players.length; k++) {
      const q = world.players[k]!
      if (k === pr.target || k === pr.owner) continue
      if (Math.abs(centerX(q) - cx) > lm.splashRadiusPx) continue
      if (Math.abs(centerY(q) - cy) > lm.splashRadiusPx) continue
      hit(world, k, 'leader_missile', lm.splashStunTicks, events, pr.owner)
    }
    return false
  },
}
